import React, { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon, TooltipComponent } from "../../../components/Component";
import { Button, Card } from "reactstrap";
import { Badge, Drawer, Table, Tooltip } from "antd";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import IssuesCreation from "./IssuesCreation";
import { issueApis } from "../../../api/Issue";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { AddAccess } from "../../../components/accessControl/AccessControl";

export interface IssueTagsInterface {
  tagId: number;
  tagName: string;
  issueTagId: number;
}

export interface IssuesListViewProps {
  issueId: number;
  issueName: string | null;
  projectTitle?: string;
  issueCreatedAt: Date | null;
  IssueAssignedToName?: string;
  IssueAssignedByName?: string;
  issueTags: IssueTagsInterface[];
  issueUpdatedAt: Date | null;
  deadlineDate: string | Date | null;
  issueStatus: string;
  priorityName?: string;
  projectId?: number;
  taskId?: number;
}

export const IssuesTable: FC<{
  taskId?: number;
  projectId?: number;
  projectName?: string;
  activeTab?: string;
  isFromWorkUI?: boolean;
  isFromSpecificLevel?: boolean;
  isFromTaskDetailPage?: boolean;
}> = ({
  taskId,
  projectId,
  projectName,
  activeTab,
  isFromWorkUI,
  isFromSpecificLevel,
  isFromTaskDetailPage,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );

  // Get task details to check if user has edit access
  const { data: TaskListInDetailModal } = useSelector(
    (state: any) => state.TaskListInDetailModal
  );
  const { selectedTaskDetails, allowEdit } = TaskListInDetailModal;

  const columns: ColumnType<IssuesListViewProps>[] = [
    {
      title: "Issue Name",
      dataIndex: "issueName",
      key: "issueName",
      width: 150,
      fixed: "left",
      render(text: any, record: IssuesListViewProps) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`issue-name-${record?.issueId}`}
              onClick={() => {
                navigate(
                  `/pms/project/issue-detail/${record?.issueId}`,
                  {
                    state: {
                      projectId: record?.projectId || projectId,
                      projectName: record?.projectTitle || projectName,
                      issueId: record?.issueId,
                      issueName: record?.issueName,
                      returnPath: location.pathname,
                      returnState: {
                        ...((location.state as any) || {}),
                        activeTab,
                      },
                    },
                  }
                );
              }}
            >
              {record?.issueName || "Demo Issue Name"}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`issue-name-${record?.issueId}`}
              direction="right"
              text={"Click To View and Edit " + record?.issueName + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Reporter",
      dataIndex: "IssueAssignedByName",
      key: "IssueAssignedByName",
      width: 150,
      render: (text: string, record: IssuesListViewProps) => {
        return <>{record?.IssueAssignedByName}</>;
      },
    },
    {
      title: "Created Time",
      dataIndex: "issueCreatedAt",
      key: "issueCreatedAt",
      width: 150,
      render: (text: string, record: IssuesListViewProps) =>
        dayjs(record?.issueCreatedAt).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: "Assignee",
      dataIndex: "IssueAssignedToName",
      key: "IssueAssignedToName",
      width: 150,
      render: (text: string, record: IssuesListViewProps) => {
        return <>{record?.IssueAssignedToName}</>;
      },
    },
    {
      title: "Tags",
      dataIndex: "issueTags",
      key: "issueTags",
      width: 150,
      render: (
        tags: IssueTagsInterface[],
        record: IssuesListViewProps,
        index: any
      ) => {
        const text = record?.issueTags
          ?.map((tag: IssueTagsInterface) => tag.tagName)
          ?.join(", ");
        return (
          <>
            <span id={`issueTags-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`issueTags-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Last Modified Time",
      dataIndex: "issueUpdatedAt",
      key: "issueUpdatedAt",
      width: 150,
      render: (text: string, record: IssuesListViewProps) =>
        dayjs(record?.issueUpdatedAt).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: "Due Date",
      dataIndex: "deadlineDate",
      key: "deadlineDate",
      width: 150,
      render: (text: string, record: IssuesListViewProps) =>
        record?.deadlineDate
          ? dayjs(record.deadlineDate).format("DD-MMM-YYYY")
          : "—",
    },
    {
      title: "Status",
      dataIndex: "issueStatus",
      key: "issueStatus",
      width: 150,
      render: (text: string, record: IssuesListViewProps) => {
        if (!record?.issueStatus) {
          return "N/A";
        }
        return <span>{record.issueStatus}</span>;
      },
    },
    {
      title: "Severity",
      dataIndex: "priorityName",
      key: "priorityName",
      width: 150,
      fixed: "right",
      render: (text) => text || "-",
    },
  ];

  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchData = async (params: any, append = false) => {
    if (activeTab !== "issues") return;
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
        projectIds,
        taskIds,
      } = params;

      const apiResponse = await issueApis.listIssues({
        page: current,
        pageSize: pageSize,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds: projectIds || projectId?.toString(),
        taskIds: taskIds || (taskId ? taskId.toString() : undefined),
      });
      // console.log("API Response:", apiResponse);

      const data = apiResponse?.data || apiResponse?.response?.data;
      if (!data || !data.list) {
        setDataSource([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        return;
      }

      const transformedList = data.list.map((item: any) => ({
        ...item,
        issueCreatedAt: item.createdAt ? new Date(item.createdAt) : null,
        issueUpdatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
        IssueAssignedByName: item.IssueAssignedByName || "—",
        IssueAssignedToName: item.IssueAssignedToName || "—",
        deadlineDate: item.deadlineDate ? new Date(item.deadlineDate) : null,
      }));

      setPagination((prev) => ({
        ...prev,
        total: Number(data.count || transformedList.length),
      }));

      setDataSource((prev: any) =>
        append ? [...prev, ...transformedList] : transformedList
      );
    } catch (error) {
      console.error("Error loading issues:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
      projectIds: projectId?.toString(),
      taskIds: taskId ? taskId.toString() : undefined,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId
            : dataSource[0]?.paginationId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData(apiParams);
  };

  const refreshListView = () => {
    try {
      const defaultPageSize = 10;
      const defaultCurrent = 1;

      setPagination({
        current: defaultCurrent,
        pageSize: defaultPageSize,
        defaultCurrent: defaultCurrent,
        defaultPageSize: defaultPageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });

      fetchData({
        current: defaultCurrent,
        pageSize: defaultPageSize,
        direction: "P", // Default direction
        sortOrder: "DESC", // Optional default sort
        pageDiff: 0,
        paginationId: null,
        projectIds: projectId?.toString(),
        taskId: taskId?.toString(),
      });
    } catch (error) {
      console.error("Error refreshing list view", error);
    }
  };

  useEffect(() => {
    if (activeTab === "issues") {
      refreshListView();
    }
  }, [projectId, activeTab]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  // useEffect(() => {
  //   fetchData({
  //     current: 1,
  //     pageSize: pagination.pageSize,
  //     sortOrder: "DESC",
  //     direction: "P",
  //     pageDiff: 0,
  //     paginationId: null,
  //   });
  // }, [projectId, taskId, activeTab]);

  return (
    <div
      className="card-inner card-inner-lg"
      style={{ minHeight: "auto", padding: 0 }}
    >
      <div className="nk-ibx-head p-0 m-0 mt-0 w-100 d-flex justify-content-between align-items-center">
        <div className="nk-ibx-head-actions w-45 ms-1">
          <div className="nk-ibx-head-tools g-1 m-0 p-0 w-100 d-flex align-items-center gap-3">
            {/* Add your filter controls here if needed */}
          </div>
        </div>

        {!isFromWorkUI && (
          <ul className="nk-ibx-head-tools g-1 m-0 p-0 d-flex align-items-center gap-3 me-2">
            <li>
              <AddAccess moduleName="Tasks">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    setModalOpen(true);
                  }}
                  disabled={
                    !allowEdit ||
                    ["closed", "archived", "onhold"].includes(
                      selectedTaskDetails?.taskStatus?.toLowerCase() || ""
                    )
                  }
                >
                  <Icon name="plus" />
                  <span>Add Issue</span>
                </Button>
              </AddAccess>
            </li>
          </ul>
        )}
      </div>

      <div className="card-inner card-inner-lg p-0">
        <div className="content clearfix" style={{ padding: 0 }}>
          {/* <div
            style={{
              width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
              margin: "10px auto",
            }}
          > */}
          <div
            className="table-responsive"
            style={{
              width: "100%",
              overflowX: "auto",
              // margin: "10px 0",
              margin: "0",
            }}
          >
            <SkeletonTable
              loading={loading}
              columns={columns as SkeletonTableColumnsType[]}
            >
              <Table
                rowKey="uid"
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                onChange={tableOnChange}
                bordered
                size="middle"
                scroll={{
                  x: 1800,
                }}
              />
            </SkeletonTable>
          </div>
        </div>
      </div>
      {!isFromWorkUI && (
        <>
          <IssuesCreation
            projectId={projectId}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            refreshData={refreshListView}
            taskDetails={selectedTaskDetails}
            disableLinkField={isFromSpecificLevel}
          />
        </>
      )}
    </div>
  );
};
